import Icon from '../../Icon'
import { NearbyVenue } from '../../../types/liveMap'
import { PIN_COLORS } from '../../../utils/liveMap/constants'
import { extractViewsCount } from '../../../utils/liveMap/computePinCounts'

/**
 * Venue detail sheet. Lists each activity stat as a row with its colored badge
 * and label, using the same colors as the map pins. Mirrors the mobile bottom
 * sheet's stat rows.
 */

interface VenueDetailSheetProps {
  venue: NearbyVenue
  onClose: () => void
}

interface StatRow {
  label: string
  subtitle: string
  value: number
  color: string
}

function buildStatRows(venue: NearbyVenue): StatRow[] {
  const rsvps = (venue.eventRsvpCount ?? 0) + (venue.visitPlanCount ?? 0)
  const rows: StatRow[] = [
    {
      label: 'Live check ins',
      subtitle: 'Here right now',
      value: venue.totalCheckins ?? 0,
      color: PIN_COLORS[0],
    },
    {
      label: 'RSVPs',
      subtitle: 'Planning to be here',
      value: rsvps || venue.rsvpCount || 0,
      color: PIN_COLORS[1],
    },
    {
      label: 'Breadcrumbs',
      subtitle: 'Notes left at the venue',
      value: venue.breadcrumbCount ?? 0,
      color: PIN_COLORS[2],
    },
    {
      label: 'Recent check ins',
      subtitle: 'In the last week',
      value: venue.recentCheckins ?? 0,
      color: PIN_COLORS[3],
    },
    {
      label: 'Recent views',
      subtitle: 'In the last week',
      value: extractViewsCount(venue),
      color: PIN_COLORS[4],
    },
    {
      label: 'Stickers',
      subtitle: 'Active stickers posted',
      value: venue.stickerCount ?? 0,
      color: PIN_COLORS[5],
    },
  ]
  return rows
}

const VenueDetailSheet = ({ venue, onClose }: VenueDetailSheetProps) => {
  const rows = buildStatRows(venue)

  return (
    <div className="venue-detail-sheet">
      <div className="venue-detail-sheet__header">
        <span className="venue-detail-sheet__logo">
          {venue.logoUrl ? (
            <img src={venue.logoUrl} alt={venue.name} />
          ) : (
            <span className="venue-detail-sheet__initials">
              {(venue.name?.trim().charAt(0) || '?').toUpperCase()}
            </span>
          )}
        </span>
        <div className="venue-detail-sheet__identity">
          <div className="venue-detail-sheet__name">{venue.name}</div>
          {venue.address && <div className="venue-detail-sheet__address">{venue.address}</div>}
        </div>
        <button
          type="button"
          className="venue-detail-sheet__close"
          aria-label="Close"
          onClick={onClose}
        >
          <Icon name="Xmark" />
        </button>
      </div>

      <ul className="venue-detail-sheet__stats">
        {rows.map(row => (
          <li key={row.label} className="venue-stat-row">
            <span className="venue-stat-row__badge" style={{ backgroundColor: row.color }}>
              {row.value}
            </span>
            <span className="venue-stat-row__text">
              <span className="venue-stat-row__label">{row.label}</span>
              <span className="venue-stat-row__subtitle">{row.subtitle}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default VenueDetailSheet
